const DEFAULT_SUPPORT_STORE_ID = '30e79df7-81e8-4c5f-a825-602e5899d961';
const LEGACY_SESSION_KEY = 'anonymous_support_session_id';
const LEGACY_CONVERSATION_KEY = 'anonymous_support_conversation';
const SESSION_PREFIX = 'anonymous_support_session_id:';
const CONVERSATION_PREFIX = 'anonymous_support_conversation:';
const ANON_SUPPORT_CONVERSATIONS_UPDATED = 'anonymous-support-conversations-updated';

export interface AnonymousSupportConversation {
  sessionId: string;
  storeId: string;
  storeName?: string;
  storeAvatarUrl?: string | null;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
}

const canUseLocalStorage = (): boolean => typeof window !== 'undefined' && !!window.localStorage;

const emitAnonymousSupportConversationsUpdated = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ANON_SUPPORT_CONVERSATIONS_UPDATED));
};

const normalizeStoreId = (storeId?: string): string => {
  const trimmed = (storeId || '').trim();
  return trimmed || DEFAULT_SUPPORT_STORE_ID;
};

const generateSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const getAnonymousSupportSessionStorageKey = (storeId?: string): string => {
  return `${SESSION_PREFIX}${normalizeStoreId(storeId)}`;
};

export const getAnonymousSupportConversationStorageKey = (storeId?: string): string => {
  return `${CONVERSATION_PREFIX}${normalizeStoreId(storeId)}`;
};

export const getAnonymousSupportSessionId = (storeId?: string): string | null => {
  if (!canUseLocalStorage()) return null;

  const normalizedStoreId = normalizeStoreId(storeId);
  const storeKey = getAnonymousSupportSessionStorageKey(normalizedStoreId);
  const stored = window.localStorage.getItem(storeKey);
  if (stored) return stored;

  if (normalizedStoreId === DEFAULT_SUPPORT_STORE_ID) {
    return window.localStorage.getItem(LEGACY_SESSION_KEY);
  }

  return null;
};

export const getOrCreateAnonymousSupportSessionId = (storeId?: string): string => {
  const normalizedStoreId = normalizeStoreId(storeId);
  const existing = getAnonymousSupportSessionId(normalizedStoreId);
  if (existing) return existing;

  const sessionId = generateSessionId();
  if (canUseLocalStorage()) {
    window.localStorage.setItem(getAnonymousSupportSessionStorageKey(normalizedStoreId), sessionId);
    if (normalizedStoreId === DEFAULT_SUPPORT_STORE_ID) {
      window.localStorage.setItem(LEGACY_SESSION_KEY, sessionId);
    }
  }

  return sessionId;
};

const safeParseConversation = (value: string | null): AnonymousSupportConversation | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.sessionId || !parsed.lastMessageAt) return null;
    return parsed as AnonymousSupportConversation;
  } catch {
    return null;
  }
};

export const getAnonymousSupportConversation = (storeId?: string): AnonymousSupportConversation | null => {
  if (!canUseLocalStorage()) return null;

  const normalizedStoreId = normalizeStoreId(storeId);
  const storeKey = getAnonymousSupportConversationStorageKey(normalizedStoreId);
  const parsed = safeParseConversation(window.localStorage.getItem(storeKey));
  if (parsed) return parsed;

  if (normalizedStoreId === DEFAULT_SUPPORT_STORE_ID) {
    const legacy = safeParseConversation(window.localStorage.getItem(LEGACY_CONVERSATION_KEY));
    if (legacy) {
      return {
        ...legacy,
        storeId: legacy.storeId || normalizedStoreId,
      };
    }
  }

  return null;
};

export const setAnonymousSupportConversation = (
  storeId: string,
  conversation: Omit<AnonymousSupportConversation, 'storeId'>
): void => {
  if (!canUseLocalStorage()) return;

  const normalizedStoreId = normalizeStoreId(storeId);
  const payload: AnonymousSupportConversation = {
    ...conversation,
    storeId: normalizedStoreId,
  };

  window.localStorage.setItem(
    getAnonymousSupportConversationStorageKey(normalizedStoreId),
    JSON.stringify(payload)
  );

  if (normalizedStoreId === DEFAULT_SUPPORT_STORE_ID) {
    window.localStorage.setItem(LEGACY_CONVERSATION_KEY, JSON.stringify(payload));
  }

  emitAnonymousSupportConversationsUpdated();
};

export const clearAnonymousSupportStoreData = (storeId?: string): void => {
  if (!canUseLocalStorage()) return;

  const normalizedStoreId = normalizeStoreId(storeId);
  window.localStorage.removeItem(getAnonymousSupportSessionStorageKey(normalizedStoreId));
  window.localStorage.removeItem(getAnonymousSupportConversationStorageKey(normalizedStoreId));

  if (normalizedStoreId === DEFAULT_SUPPORT_STORE_ID) {
    window.localStorage.removeItem(LEGACY_SESSION_KEY);
    window.localStorage.removeItem(LEGACY_CONVERSATION_KEY);
  }

  emitAnonymousSupportConversationsUpdated();
};

export const clearAnonymousSupportConversationOnly = (storeId?: string): void => {
  if (!canUseLocalStorage()) return;

  const normalizedStoreId = normalizeStoreId(storeId);
  window.localStorage.removeItem(getAnonymousSupportConversationStorageKey(normalizedStoreId));

  if (normalizedStoreId === DEFAULT_SUPPORT_STORE_ID) {
    window.localStorage.removeItem(LEGACY_CONVERSATION_KEY);
  }

  emitAnonymousSupportConversationsUpdated();
};

export const listAnonymousSupportSessionEntries = (): Array<{ storeId: string; sessionId: string }> => {
  if (!canUseLocalStorage()) return [];

  const entries = new Map<string, string>();

  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key) continue;
    if (!key.startsWith(SESSION_PREFIX)) continue;

    const storeId = key.slice(SESSION_PREFIX.length);
    const sessionId = window.localStorage.getItem(key);
    if (storeId && sessionId) {
      entries.set(storeId, sessionId);
    }
  }

  const legacySessionId = window.localStorage.getItem(LEGACY_SESSION_KEY);
  if (legacySessionId && !entries.has(DEFAULT_SUPPORT_STORE_ID)) {
    entries.set(DEFAULT_SUPPORT_STORE_ID, legacySessionId);
  }

  return Array.from(entries.entries()).map(([storeId, sessionId]) => ({ storeId, sessionId }));
};

export const listAnonymousSupportConversations = (): AnonymousSupportConversation[] => {
  if (!canUseLocalStorage()) return [];

  const conversations = new Map<string, AnonymousSupportConversation>();

  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key) continue;
    if (!key.startsWith(CONVERSATION_PREFIX)) continue;

    const parsed = safeParseConversation(window.localStorage.getItem(key));
    if (!parsed) continue;

    const storeId = key.slice(CONVERSATION_PREFIX.length) || parsed.storeId || DEFAULT_SUPPORT_STORE_ID;
    conversations.set(storeId, {
      ...parsed,
      storeId,
    });
  }

  const legacy = safeParseConversation(window.localStorage.getItem(LEGACY_CONVERSATION_KEY));
  if (legacy && !conversations.has(DEFAULT_SUPPORT_STORE_ID)) {
    conversations.set(DEFAULT_SUPPORT_STORE_ID, {
      ...legacy,
      storeId: legacy.storeId || DEFAULT_SUPPORT_STORE_ID,
    });
  }

  return Array.from(conversations.values()).sort((a, b) => {
    return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
  });
};

export const getDefaultSupportStoreId = (): string => DEFAULT_SUPPORT_STORE_ID;
